import type { RecordField } from './RecordActions';

// 各模块行级编辑弹窗的字段配置（与 core-api EDITABLE_RECORDS 白名单对齐；
// 外键类字段（客户/物料指向）不在行级弹窗中暴露，避免误改归属）。
const STATUS = {
  quotation: ['draft', 'sent', 'accepted', 'rejected', 'expired', 'cancelled'],
  contract: ['draft', 'confirmed', 'in_production', 'ready', 'partially_delivered', 'completed', 'cancelled'],
  delivery: ['draft', 'confirmed', 'dispatched', 'delivered', 'cancelled'],
  // 注意：voided 不进这个下拉。行级编辑是 PATCH /api/records/:type/:id 的自由改写，
  // 作废必须走 POST /api/work-orders/:id/void —— 那条路径会校验「无任何业务事实」
  // 并强制填写原因与留痕。若放进通用下拉，管理员就能绕过这些约束把有事实的工单改成作废。
  'work-order': ['draft', 'released', 'in_progress', 'completed', 'cancelled'],
  'purchase-order': ['draft', 'confirmed', 'partially_received', 'received', 'cancelled'],
  return: ['draft', 'authorized', 'received', 'inspected', 'resolved', 'cancelled'],
  inspectionResult: ['pending', 'accepted', 'rejected', 'conditional'],
  warehouseType: ['stock', 'raw_material', 'work_in_progress', 'finished_goods', 'returns', 'scrap'],
  asset: ['active', 'in_service', 'delivered', 'retired', 'scrapped'],
  csm: ['received', 'in_process', 'awaiting_return', 'returned', 'scrapped'],
  csmScenario: ['repair', 'processing', 'other'],
  inventoryPolicy: ['powder', 'consumable', 'normal', 'none'],
};

export const RECORD_FIELDS: Record<string, RecordField[]> = {
  customers: [
    { key: 'legal_name', label: '法定名称', required: true },
    { key: 'trade_name', label: '经营名称' },
    { key: 'code', label: '客户编码' },
    { key: 'tax_id', label: '税号 / CUIT' },
    { key: 'tax_category', label: '税务类别' },
    { key: 'currency', label: '币种' },
    { key: 'payment_terms_days', label: '账期（天）', type: 'number' },
    { key: 'email', label: '邮箱' },
    { key: 'phone', label: '电话' },
    { key: 'city', label: '城市' },
    { key: 'province', label: '省份' },
    { key: 'postal_code', label: '邮编' },
    { key: 'country', label: '国家' },
    { key: 'address', label: '地址' },
  ],
  suppliers: [
    { key: 'legal_name', label: '供应商名称', required: true },
    { key: 'code', label: '供应商编码' },
    { key: 'tax_id', label: '税号' },
    { key: 'email', label: '邮箱' },
    { key: 'phone', label: '电话' },
    { key: 'address', label: '地址' },
  ],
  items: [
    { key: 'name_zh', label: '名称（中）', required: true },
    { key: 'name_es', label: '名称（西）' },
    { key: 'code', label: '物料编码' },
    { key: 'item_group', label: '物料组' },
    { key: 'item_type', label: '物料类型' },
    { key: 'inventory_policy', label: '库存策略', type: 'select', options: STATUS.inventoryPolicy },
    { key: 'stock_uom', label: '库存单位' },
    { key: 'minimum_qty', label: '最低库存', type: 'number' },
    { key: 'reorder_qty', label: '补货量', type: 'number' },
    { key: 'warning_enabled', label: '启用预警', type: 'checkbox' },
    { key: 'sale_price', label: '销售价格', type: 'number' },
    // 采购成本仅 finance/admin；ItemsPage 会按 canManagePurchaseCost 过滤本字段
    { key: 'purchase_cost', label: '采购成本', type: 'number' },
    { key: 'currency', label: '币种' },
    { key: 'description_es', label: '西语描述' },
    { key: 'active', label: '启用', type: 'checkbox' },
  ],
  warehouses: [
    { key: 'code', label: '仓库编码', required: true },
    { key: 'name_zh', label: '名称（中）' },
    { key: 'name_es', label: '名称（西）' },
    { key: 'warehouse_type', label: '仓库类型', type: 'select', options: STATUS.warehouseType },
    { key: 'active', label: '启用', type: 'checkbox' },
  ],
  'price-lists': [
    { key: 'name', label: '价目表名称', required: true },
    { key: 'currency', label: '币种' },
    { key: 'valid_from', label: '生效自', type: 'date' },
    { key: 'valid_until', label: '生效至', type: 'date' },
    { key: 'notes', label: '备注' },
    { key: 'active', label: '启用', type: 'checkbox' },
  ],
  boms: [
    { key: 'quantity', label: '标准数量', type: 'number' },
    { key: 'version', label: '版本' },
    { key: 'notes', label: '备注' },
    { key: 'active', label: '启用', type: 'checkbox' },
  ],
  operations: [
    { key: 'name_zh', label: '工序名称（中）', required: true },
    { key: 'name_es', label: '工序名称（西）' },
    { key: 'code', label: '工序编码' },
    { key: 'workstation', label: '工位' },
    { key: 'default_minutes', label: '默认工时（分）', type: 'number' },
    { key: 'hourly_cost', label: '小时成本', type: 'number' },
    { key: 'qc_required', label: '需生成检验任务', type: 'checkbox' },
    { key: 'active', label: '启用', type: 'checkbox' },
  ],
  quotations: [
    { key: 'quotation_date', label: '报价日期', type: 'date' },
    { key: 'valid_until', label: '有效期限', type: 'date' },
    { key: 'status', label: '状态', type: 'select', options: STATUS.quotation },
    { key: 'notes_zh', label: '备注（中）' },
    { key: 'terms_es', label: '条款（西）' },
  ],
  contracts: [
    { key: 'contract_date', label: '合同日期', type: 'date' },
    { key: 'delivery_due_date', label: '交货期限', type: 'date' },
    { key: 'status', label: '状态', type: 'select', options: STATUS.contract },
    { key: 'terms_es', label: '条款（西）' },
  ],
  deliveries: [
    { key: 'delivery_date', label: '交货日期', type: 'date' },
    { key: 'carrier', label: '承运人' },
    { key: 'tracking_number', label: '运单号' },
    { key: 'status', label: '状态', type: 'select', options: STATUS.delivery },
    { key: 'notes_es', label: '备注（西）' },
  ],
  'purchase-orders': [
    { key: 'order_date', label: '下单日期', type: 'date' },
    { key: 'expected_date', label: '期望到货', type: 'date' },
    { key: 'status', label: '状态', type: 'select', options: STATUS['purchase-order'] },
    { key: 'notes', label: '备注' },
  ],
  'work-orders': [
    { key: 'planned_start_date', label: '计划开始', type: 'date' },
    { key: 'planned_end_date', label: '计划完成', type: 'date' },
    { key: 'status', label: '状态', type: 'select', options: STATUS['work-order'] },
  ],
  inspections: [
    { key: 'inspection_date', label: '检验日期', type: 'date' },
    { key: 'inspector_name', label: '检验人', required: true },
    { key: 'contract_id', label: '关联合同 ID' },
    { key: 'work_order_id', label: '关联工单 ID' },
    { key: 'delivery_id', label: '关联交货单 ID（交货后补）' },
    { key: 'serial_number', label: '序列号' },
    { key: 'item_id', label: '物料 ID' },
    { key: 'qc_template_id', label: '质检模板 ID' },
    { key: 'process_type', label: '工艺类型' },
    { key: 'result', label: '结论（默认待检）', type: 'select', options: STATUS.inspectionResult },
    { key: 'remarks_zh', label: '备注（中）' },
    { key: 'remarks_es', label: '备注（西）' },
  ],
  returns: [
    { key: 'return_date', label: '退货日期', type: 'date' },
    { key: 'reason_code', label: '原因代码' },
    { key: 'reason_detail', label: '原因说明' },
    { key: 'disposition', label: '处置方式' },
    { key: 'status', label: '状态', type: 'select', options: STATUS.return },
  ],
  'physical-assets': [
    { key: 'customer_serial', label: '客户序列号', required: true },
    { key: 'internal_code', label: '内部编码' },
    { key: 'asset_type', label: '工件类型' },
    { key: 'description', label: '描述' },
    { key: 'first_received_date', label: '首次收件日期', type: 'date' },
    { key: 'status', label: '状态', type: 'select', options: STATUS.asset },
    { key: 'notes', label: '备注' },
  ],
  'customer-supplied-materials': [
    { key: 'scenario', label: '业务场景', type: 'select', options: STATUS.csmScenario },
    { key: 'description', label: '说明' },
    { key: 'serial_number', label: '序列号' },
    { key: 'quantity', label: '数量', type: 'number' },
    { key: 'uom', label: '单位' },
    { key: 'incoming_condition', label: '来料状态' },
    { key: 'received_date', label: '收件日期', type: 'date' },
    { key: 'source_reference', label: '来源单据' },
    { key: 'storage_location', label: '存放位置' },
    { key: 'status', label: '状态', type: 'select', options: STATUS.csm },
    { key: 'returned_date', label: '归还日期', type: 'date' },
    { key: 'notes', label: '备注' },
  ],
};
